import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { ToastProvider } from './context/ToastContext';
import { PlanProvider } from './context/PlanContext';
import { RefreshProvider } from './context/RefreshContext';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Acervo from './pages/Acervo';
import LandingPage from './pages/LandingPage';
import BibliotecaPublica from './pages/BibliotecaPublica';
import ClubePublico from './pages/ClubePublico';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import TermosUso from './pages/TermosUso';
import Suporte from './pages/Suporte';
import ConviteEmprestimo from './pages/ConviteEmprestimo';
import Admin from './pages/Admin';

async function criarPerfilSeNecessario(user) {
  try {
    const { data: perfilExistente } = await supabase
      .from('perfis').select('id').eq('id', user.id).maybeSingle();

    if (perfilExistente) return;

    const nome = user.user_metadata?.nome || user.email.split('@')[0];
    const username = user.user_metadata?.username || null;
    const whatsapp = user.user_metadata?.whatsapp || null;
    const base = nome.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const slugBase = base || `usuario-${Date.now()}`;

    let slug = slugBase;
    let contador = 1;
    while (true) {
      const { data: slugExistente } = await supabase
        .from('perfis').select('id').eq('slug', slug).maybeSingle();
      if (!slugExistente) break;
      slug = `${slugBase}-${contador}`;
      contador++;
    }

    await supabase.from('perfis').insert({ id: user.id, nome, slug, username, whatsapp });

    // Auto-inscrever no Clube de Boas-Vindas
    const { data: clubeBV } = await supabase
      .from('clubes').select('id').eq('slug', 'clube-de-boas-vindas').maybeSingle();
    if (clubeBV?.id) {
      await supabase.from('clube_membros').insert({
        clube_id: clubeBV.id,
        user_id: user.id,
        status: 'aprovado',
        role: 'membro',
      });
    }
  } catch (err) {
    console.error('Erro ao criar perfil:', err);
  }
}

function AppContent() {
  const [sessao, setSessao] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSessao(session);
      if (session?.user) {
        criarPerfilSeNecessario(session.user);
        const redirect = localStorage.getItem('redirect_after_login');
        if (redirect) {
          localStorage.removeItem('redirect_after_login');
          navigate(redirect);
        }
      }
      setCarregando(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSessao(session);
      if (session?.user) {
        await criarPerfilSeNecessario(session.user);
        if (localStorage.getItem('nova_conta') === '1') {
          localStorage.removeItem('nova_conta');
          // Só redireciona se o usuário não está já em /home (navegou explicitamente)
          if (window.location.pathname !== '/home') {
            navigate('/onboarding');
          }
        } else {
          const redirect = localStorage.getItem('redirect_after_login');
          if (redirect) {
            localStorage.removeItem('redirect_after_login');
            navigate(redirect);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (carregando) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-400">Carregando...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Públicas */}
      <Route path="/biblioteca/:slug" element={<BibliotecaPublica />} />
      <Route path="/clube/:slug" element={<ClubePublico />} />
      <Route path="/convite/emprestimo/:id" element={<ConviteEmprestimo />} />
      <Route path="/privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/termos" element={<TermosUso />} />
      <Route path="/suporte" element={<Suporte />} />

      {/* Landing — sempre acessível */}
      <Route path="/" element={<LandingPage />} />

      {/* Autenticação */}
      <Route path="/login" element={sessao ? <Navigate to="/home" replace /> : <Login />} />
      <Route path="/cadastro" element={sessao ? <Navigate to="/home" replace /> : <Login modoInicial="cadastrar" />} />

      {/* Onboarding — exibido uma vez após cadastro */}
      <Route path="/onboarding" element={sessao ? (
        <Onboarding
          userId={sessao.user.id}
          nomeUsuario={sessao.user.user_metadata?.nome || ''}
          onConcluir={() => navigate('/perfil')}
          onPular={() => navigate('/perfil')}
        />
      ) : <Navigate to="/login" replace />} />

      {/* App principal — protegido, containment de largura de celular no desktop */}
      <Route path="/home" element={sessao ? (
        <div className="max-w-md mx-auto lg:max-w-none lg:mx-0 bg-paper overflow-hidden" style={{transform:'translateZ(0)', height:'100dvh'}}>
          <Acervo />
        </div>
      ) : <Navigate to="/login" replace />} />

      {/* Rota legada — redireciona para /home */}
      <Route path="/acervo" element={<Navigate to="/home" replace />} />

      {/* Admin */}
      <Route path="/admin" element={<Admin />} />

      {/* Catch-all */}
      <Route path="*" element={sessao ? <Navigate to="/home" replace /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ToastProvider>
      <RefreshProvider>
        <PlanProvider>
          <AppContent />
        </PlanProvider>
      </RefreshProvider>
    </ToastProvider>
  );
}

export default App;